import { computed, resource, type Signal, signal, untracked } from '@angular/core';
import { resourceSettler } from './resource-settled';

export type LoadOnceResource<T> = {
  /** The loaded value, or undefined until the first load has landed. */
  readonly value: Signal<T | undefined>;
  /**
   * Starts the load the first time it is called and resolves once the value
   * has landed; later calls resolve at once without reading again. Rejects
   * with the load error, and a failed load is read again on the next call.
   */
  readonly load: () => Promise<void>;
  /** True once a read has failed, until a later read lands. */
  readonly failed: Signal<boolean>;
};

/**
 * Global reference data fetched on first request and kept for the session.
 *
 * Load-once is structural here: the resource's parameter is the request
 * itself, so there is no "is it empty yet" guard for a caller to get wrong,
 * and concurrent callers share the one read in flight.
 *
 * Call in an injection context (a field initializer).
 */
export function loadOnceResource<T>(loader: () => Promise<T>): LoadOnceResource<T> {
  const requested = signal(false);
  const res = resource<T, true | undefined>({
    params: () => requested() || undefined,
    loader: () => loader(),
  });
  const settled = resourceSettler(res);
  return {
    value: computed(() => (res.hasValue() ? res.value() : undefined)),
    failed: computed(() => res.status() === 'error'),
    load: () => {
      if (untracked(res.status) === 'error') res.reload();
      else requested.set(true);
      return settled();
    },
  };
}
